import { StateCreator } from 'zustand'
import {
    TrafficSourceOptionType,
    DetalizationOptionType,
    UsersSortOptionType,
    typedDetalizationSortOptions,
    typedTrafficSourceSortOptions,
    typedUsersSortOptions,
    SubscriberStatusSortOptionType,
    typedSubscriberStatusSortOptions,
    LineDateSortOptionType,
    typedLineDateSortOptions,
} from '../../constants'
import { InviteLinksState } from '../state'
import { getSubscribers } from '@/shared/api/telegram/getSubscribers'
import { StartEndDateByChannelType, StartEndDateInterface } from '../types'
import { SubscriberResponse } from '@synapex/shared'
import { RKNStatus } from '@synapex/prisma/client'

export interface AudienceTableRowType {
    id: number
    name?: string
    username?: string
    source?: string
    link?: string
    telegramId: string
    subscriptionDate?: string
    unsubscriptionDate?: string
    timeInChannel: string
    isPremium: boolean
    userPhotoUrl: string | null
    page?: number
    limit?: number
    title: string | null
    rknStatus: RKNStatus | null
    sourcePhotoUrl: string | null
}

type AudienceByChannelType = Record<number, AudienceTableRowType[]>

export interface AudienceSlice {
    audienceByChannel: AudienceByChannelType
    loadingAudience: boolean
    errorAudience: any | null
    updatedAtAudience: string
    trafficSourceSortAudience: TrafficSourceOptionType
    detalizationSortAudience: DetalizationOptionType
    usersSortAudience: UsersSortOptionType
    subscriberStatusSortAudience: SubscriberStatusSortOptionType
    startDateAudience: string
    endDateAudience: string
    startEndDateByChannel: StartEndDateByChannelType
    lineDateAudience: LineDateSortOptionType | null
    fetchAudienceByChannel: (
        channelId: number,
        channelCreatedAt: Date,
        page: number,
        limit: number
    ) => Promise<void>
    getAudienceByChannel: (
        channelId: number
    ) => AudienceTableRowType[] | undefined
    setStartDateAudience: (value: string) => void
    setEndDateAudience: (value: string) => void
    setTrafficSourceSortAudience: (value: TrafficSourceOptionType) => void
    setDetalizationSortAudience: (value: DetalizationOptionType) => void
    setUsersSortAudience: (value: UsersSortOptionType) => void
    setSubscriberStatusSortAudience: (
        value: SubscriberStatusSortOptionType
    ) => void
    calculateStartEndDateAudience: (
        channelId: number,
        force?: boolean
    ) => StartEndDateInterface
    setLineDateAudience: (value: LineDateSortOptionType | null) => void
    sortConfigAudience: {
        key: keyof AudienceTableRowType
        direction: 'asc' | 'desc'
    } | null
    searchQueryAudience: string
    setSearchQueryAudience: (value: string) => void
    setSortConfigAudience: (
        config: {
            key: keyof AudienceTableRowType
            direction: 'asc' | 'desc'
        } | null
    ) => void
    currentPageAudience: Record<number, number>
    totalPagesAudience: Record<number, number>
}

export const createAudienceSlice: StateCreator<
    InviteLinksState,
    [],
    [],
    AudienceSlice
> = (set, get) => ({
    sortConfigAudience: { key: 'subscriptionDate', direction: 'desc' },
    searchQueryAudience: '',
    audienceByChannel: {},
    loadingAudience: false,
    errorAudience: null,
    updatedAtAudience: '',

    trafficSourceSortAudience: typedTrafficSourceSortOptions[2],
    detalizationSortAudience: typedDetalizationSortOptions[0],
    usersSortAudience: typedUsersSortOptions[0],
    subscriberStatusSortAudience: typedSubscriberStatusSortOptions[0],
    startDateAudience: '',
    endDateAudience: '',
    startEndDateByChannel: {},
    lineDateAudience: typedLineDateSortOptions[2],
    currentPageAudience: {},
    totalPagesAudience: {},

    setLineDateAudience: (value) => set({ lineDateAudience: value }),

    fetchAudienceByChannel: async (
        channelId: number,
        channelCreatedAt: Date,
        page = 1,
        limit = 10
    ) => {
        const data = await getSubscribers({
            channelId: channelId,
            page: page,
            limit: limit,
        })
        const audienceTableData: AudienceTableRowType[] = await Promise.all(
            data.data.map((subscriber) =>
                convertToAudienceTableRow(subscriber, channelCreatedAt)
            )
        )

        set((state) => ({
            audienceByChannel: {
                ...state.audienceByChannel,
                [channelId]: audienceTableData,
            },
            loading: false,
            updatedAt: new Date().toTimeString(),
            currentPageAudience: {
                ...state.currentPageAudience,
                [channelId]: page,
            },
            totalPagesAudience: {
                ...state.totalPagesAudience,
                [channelId]: data.meta.totalPages,
            },
        }))
    },

    getAudienceByChannel: (channelId: number) => {
        const { audienceByChannel } = get()
        return audienceByChannel[channelId]
    },

    setStartDateAudience: (value) => set({ startDateAudience: value }),
    setEndDateAudience: (value) => set({ endDateAudience: value }),
    setTrafficSourceSortAudience: (value) =>
        set({ trafficSourceSortAudience: value }),
    setDetalizationSortAudience: (value) =>
        set({ detalizationSortAudience: value }),
    setUsersSortAudience: (value) => set({ usersSortAudience: value }),
    setSubscriberStatusSortAudience: (value) =>
        set({ subscriberStatusSortAudience: value }),

    calculateStartEndDateAudience: (
        channelId: number,
        force = false
    ) => {
        const { startEndDateByChannel } = get()

        if (startEndDateByChannel[channelId] && !force) {
            return startEndDateByChannel[channelId]
        }

        const startDate = new Date(
            new Date().setDate(new Date().getDate() - 6)
        ).toISOString()
        const endDate = new Date().toISOString()
        const dates = {
            startDate: startDate,
            endDate: endDate,
        }
        set((state) => ({
            startEndDateByChannel: {
                ...state.startEndDateByChannel,
                [channelId]: dates,
            },
        }))
        get().setStartDateAudience(startDate)
        get().setEndDateAudience(endDate)

        set({ startDateAudience: startDate })
        set({ endDateAudience: endDate })

        return dates
    },

    setSortConfigAudience: (
        config: {
            key: keyof AudienceTableRowType
            direction: 'asc' | 'desc'
        } | null
    ) => {
        set({ sortConfigAudience: config })
    },

    setSearchQueryAudience: (query: string) => {
        set({ searchQueryAudience: query })
    },

    clearSortAndSearchAudience: () => {
        set({
            sortConfigAudience: null,
            searchQueryAudience: '',
        })
    },
})

export async function convertToAudienceTableRow(
    subscriber: SubscriberResponse,
    channelCreatedAt: Date | undefined
): Promise<AudienceTableRowType> {
    const {
        subscriber_id,
        invite_link_name,
        invite_link,
        full_name,
        username,
        telegram_id,
        joined_at,
        left_at,
        is_premium,
        title,
        rknStatus,
        small_photo_data_url,
        sourcePhotoUrl,
    } = subscriber

    const timeInChannel = calculateTimeInChannel(
        joined_at ? new Date(joined_at) : null,
        left_at ? new Date(left_at) : null,
        channelCreatedAt ? new Date(channelCreatedAt) : null
    )

    return {
        id: subscriber_id,
        name: full_name,
        username: username,
        userPhotoUrl: small_photo_data_url,
        source: joined_at ? invite_link_name : 'Начальная аудитория',
        link: invite_link,
        telegramId: telegram_id,
        subscriptionDate: joined_at,
        unsubscriptionDate: left_at ?? undefined,
        timeInChannel: timeInChannel,
        isPremium: is_premium,
        title: title,
        rknStatus,
        sourcePhotoUrl,
    }
}

function createLabel(number: number, titles: string[]) {
    const cases = [2, 0, 1, 1, 1, 2]
    return titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]]
}

function calculateTimeInChannel(
    joined_at: Date | null,
    left_at: Date | null,
    channelCreatedAt: Date | null
): string {
    const joinedDate = joined_at
        ? new Date(joined_at)
        : channelCreatedAt
          ? new Date(channelCreatedAt)
          : new Date()
    const leftDate = left_at ? new Date(left_at) : new Date()
    const diffTime = Math.abs(leftDate.getTime() - joinedDate.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    return `${diffDays} ${createLabel(diffDays, ['день', 'дня', 'дней'])}`
}
